/**
 * Pure cashbook backup payload helpers (validate, Excel sheets, ZIP bytes).
 */

import * as XLSX from 'xlsx';
import JSZip from 'jszip';

export const CASHBOOK_BACKUP_KIND = 'finvox-cashbook' as const;
export const CASHBOOK_BACKUP_VERSION = 1 as const;
export const CASHBOOK_BACKUP_CONFIRM_PHRASE_EN = 'RESTORE';

export type CashbookBackupDoc = {
  id: string;
  data: Record<string, unknown>;
};

export type CashbookBackupPayload = {
  version: typeof CASHBOOK_BACKUP_VERSION;
  kind: typeof CASHBOOK_BACKUP_KIND;
  orgId: string;
  exportedAt: string;
  settings: Record<string, unknown>;
  entries: CashbookBackupDoc[];
  debtEntries: CashbookBackupDoc[];
};

export type CashbookBackupValidation =
  | { ok: true; payload: CashbookBackupPayload }
  | { ok: false; code: string; message: string };

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isBackupDoc(v: unknown): v is CashbookBackupDoc {
  if (!isPlainObject(v)) return false;
  if (typeof v.id !== 'string' || !v.id.trim()) return false;
  return isPlainObject(v.data);
}

/** Serialize Firestore field values for JSON (Timestamps → ISO). */
export function serializeFirestoreValue(value: unknown): unknown {
  if (value == null) return value;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    try {
      return (value as { toDate: () => Date }).toDate().toISOString();
    } catch {
      return null;
    }
  }
  if (Array.isArray(value)) return value.map(serializeFirestoreValue);
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = serializeFirestoreValue(v);
    }
    return out;
  }
  return value;
}

export function serializeDocData(data: Record<string, unknown>): Record<string, unknown> {
  return serializeFirestoreValue(data) as Record<string, unknown>;
}

export function buildCashbookBackupPayload(input: {
  orgId: string;
  settings: Record<string, unknown>;
  entries: CashbookBackupDoc[];
  debtEntries: CashbookBackupDoc[];
  exportedAt?: string;
}): CashbookBackupPayload {
  return {
    version: CASHBOOK_BACKUP_VERSION,
    kind: CASHBOOK_BACKUP_KIND,
    orgId: input.orgId,
    exportedAt: input.exportedAt ?? new Date().toISOString(),
    settings: serializeDocData(input.settings),
    entries: input.entries.map((e) => ({ id: e.id, data: serializeDocData(e.data) })),
    debtEntries: input.debtEntries.map((e) => ({
      id: e.id,
      data: serializeDocData(e.data)
    }))
  };
}

export function validateCashbookBackupPayload(
  raw: unknown,
  expectedOrgId: string
): CashbookBackupValidation {
  if (!isPlainObject(raw)) {
    return { ok: false, code: 'invalid_json', message: 'Backup file is not valid JSON' };
  }
  if (raw.kind !== CASHBOOK_BACKUP_KIND) {
    return { ok: false, code: 'wrong_kind', message: 'Not a FinVox cashbook backup' };
  }
  if (raw.version !== CASHBOOK_BACKUP_VERSION) {
    return {
      ok: false,
      code: 'unsupported_version',
      message: `Unsupported backup version: ${String(raw.version)}`
    };
  }
  if (typeof raw.orgId !== 'string' || !raw.orgId.trim()) {
    return { ok: false, code: 'missing_org', message: 'Backup is missing organization id' };
  }
  if (raw.orgId !== expectedOrgId) {
    return {
      ok: false,
      code: 'org_mismatch',
      message: 'Backup belongs to a different organization'
    };
  }
  if (!isPlainObject(raw.settings)) {
    return { ok: false, code: 'invalid_settings', message: 'Backup settings are invalid' };
  }
  if (!Array.isArray(raw.entries) || !raw.entries.every(isBackupDoc)) {
    return { ok: false, code: 'invalid_entries', message: 'Backup cash entries are invalid' };
  }
  if (!Array.isArray(raw.debtEntries) || !raw.debtEntries.every(isBackupDoc)) {
    return { ok: false, code: 'invalid_debt_entries', message: 'Backup debt entries are invalid' };
  }
  if (typeof raw.exportedAt !== 'string') {
    return { ok: false, code: 'invalid_exported_at', message: 'Backup export date is invalid' };
  }

  return {
    ok: true,
    payload: {
      version: CASHBOOK_BACKUP_VERSION,
      kind: CASHBOOK_BACKUP_KIND,
      orgId: raw.orgId,
      exportedAt: raw.exportedAt,
      settings: raw.settings,
      entries: raw.entries,
      debtEntries: raw.debtEntries
    }
  };
}

function sheetFromRows(rows: (string | number)[][]): Uint8Array {
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  return XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as Uint8Array;
}

export function buildCashbookBackupExcelFiles(payload: CashbookBackupPayload): Record<string, Uint8Array> {
  const settingsRows: (string | number)[][] = [
    ['Field', 'Value'],
    ['openingAmount', String(payload.settings.openingAmount ?? '')],
    ['openingDebtAmount', String(payload.settings.openingDebtAmount ?? '')],
    ['openingDate', String(payload.settings.openingDate ?? '')],
    ['openingSetBy', String(payload.settings.openingSetBy ?? '')],
    ['exportedAt', payload.exportedAt],
    ['orgId', payload.orgId],
    [],
    ['Opening debt lines'],
    ['id', 'creditor', 'amount', 'categoryKey', 'memo']
  ];
  const lines = Array.isArray(payload.settings.openingDebtLines)
    ? payload.settings.openingDebtLines
    : [];
  for (const line of lines) {
    if (!isPlainObject(line)) continue;
    settingsRows.push([
      String(line.id ?? ''),
      String(line.creditor ?? ''),
      String(line.amount ?? ''),
      String(line.categoryKey ?? ''),
      String(line.memo ?? '')
    ]);
  }

  const cashRows: (string | number)[][] = [
    [
      'id',
      'type',
      'amount',
      'date',
      'categoryKey',
      'memo',
      'status',
      'periodId',
      'createdBy',
      'linkedDebtEntryId',
      'source',
      'payvoxSaleId',
      'payvoxDayKey',
      'fundingSource',
      'drawerCount',
      'salesDayKey',
      'salesOrigin'
    ]
  ];
  for (const entry of payload.entries) {
    const d = entry.data;
    cashRows.push([
      entry.id,
      String(d.type ?? ''),
      String(d.amount ?? ''),
      String(d.date ?? ''),
      String(d.categoryKey ?? ''),
      String(d.memo ?? ''),
      String(d.status ?? ''),
      String(d.periodId ?? ''),
      String(d.createdBy ?? ''),
      String(d.linkedDebtEntryId ?? ''),
      String(d.source ?? ''),
      String(d.payvoxSaleId ?? ''),
      String(d.payvoxDayKey ?? ''),
      String(d.fundingSource ?? ''),
      String(d.drawerCount ?? ''),
      String(d.salesDayKey ?? ''),
      String(d.salesOrigin ?? '')
    ]);
  }

  const debtRows: (string | number)[][] = [
    [
      'id',
      'type',
      'amount',
      'date',
      'categoryKey',
      'creditor',
      'memo',
      'status',
      'periodId',
      'createdBy',
      'linkedCashbookEntryId',
      'appliedToDebtEntryId',
      'allocations'
    ]
  ];
  for (const entry of payload.debtEntries) {
    const d = entry.data;
    debtRows.push([
      entry.id,
      String(d.type ?? ''),
      String(d.amount ?? ''),
      String(d.date ?? ''),
      String(d.categoryKey ?? ''),
      String(d.creditor ?? ''),
      String(d.memo ?? ''),
      String(d.status ?? ''),
      String(d.periodId ?? ''),
      String(d.createdBy ?? ''),
      String(d.linkedCashbookEntryId ?? ''),
      String(d.appliedToDebtEntryId ?? ''),
      d.allocations != null ? JSON.stringify(d.allocations) : ''
    ]);
  }

  return {
    'الإعدادات-والافتتاح.xlsx': sheetFromRows(settingsRows),
    'سجل-النقد.xlsx': sheetFromRows(cashRows),
    'سجل-الديون.xlsx': sheetFromRows(debtRows)
  };
}

export function buildCashbookBackupReadme(payload: CashbookBackupPayload): string {
  return [
    'FinVox cashbook offline backup',
    `orgId: ${payload.orgId}`,
    `exportedAt: ${payload.exportedAt}`,
    `entries: ${payload.entries.length}`,
    `debtEntries: ${payload.debtEntries.length}`,
    '',
    'Excel sheets: full cashbook history.',
    'JSON file: full restore payload (use Restore JSON in Danger Zone).',
    'Restore overwrites all cashbook settings, cash entries, and debt entries for this organization.'
  ].join('\n');
}

export async function buildCashbookBackupZipBytes(payload: CashbookBackupPayload): Promise<Uint8Array> {
  const zip = new JSZip();
  const jsonName = 'نسخة-البيانات-الكاملة.json';
  zip.file(jsonName, JSON.stringify(payload, null, 2));
  zip.file('README.txt', buildCashbookBackupReadme(payload));
  const sheets = buildCashbookBackupExcelFiles(payload);
  for (const [name, bytes] of Object.entries(sheets)) {
    zip.file(name, bytes);
  }
  const out = await zip.generateAsync({ type: 'uint8array', compression: 'DEFLATE' });
  return out;
}

export function cashbookBackupJsonFilename(orgId: string, exportedAt: string): string {
  const day = exportedAt.slice(0, 10) || 'backup';
  return `finvox-cashbook-${orgId.slice(0, 8)}-${day}.json`;
}

export function cashbookBackupZipFilename(exportedAt: string): string {
  const day = exportedAt.slice(0, 10) || 'backup';
  return `نسخة-احتياطية-سجل-نقدي-${day}.zip`;
}

export function lastJsonBackupStorageKey(orgId: string): string {
  return `cashbook.lastJsonBackupAt.${orgId}`;
}

export function readLastJsonBackupAt(orgId: string): string | null {
  try {
    const v = localStorage.getItem(lastJsonBackupStorageKey(orgId));
    return v && v.trim() ? v : null;
  } catch {
    return null;
  }
}

export function writeLastJsonBackupAt(orgId: string, iso: string): void {
  try {
    localStorage.setItem(lastJsonBackupStorageKey(orgId), iso);
  } catch {
    /* ignore quota / private mode */
  }
}
